// The coach panel (formerly Ask Nexus): the personal assistant and built-in
// coach as one right-side glass slide-over,
// mounted once in the workspace Layout (outside page content). It opens on
// the `nexus:ask-open` window event or Ctrl/⌘+J, closes on Esc, the close
// button or Ctrl/⌘+J again, and leaves the page usable beside it (it is a
// complementary panel, not a modal). The assistant itself (conversations,
// thread, run states and the explicit approval of audited actions) loads
// only while the panel is open.

import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  ASK_NEXUS_CLOSE_EVENT,
  ASK_NEXUS_OPEN_EVENT,
  isAskNexusOpen,
  rememberAskNexusOpen,
} from "./askNexusStore";
import { MS, usePresence } from "../ui/motion";
import "./askNexus.css";

const AskNexus = lazy(() => import("../../pages/VirtualManager"));

const isToggleKey = (event) =>
  (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "j";

const PanelLoading = ({ onClose }) => (
  <aside className="nx-ask" role="dialog" aria-label="Coach" aria-busy="true">
    <div className="nx-ask-head">
      <span className="nx-ask-title">Coach</span>
      <button
        type="button"
        className="sg-btn sg-btn-ghost sg-btn-icon"
        aria-label="Close coach"
        onClick={onClose}
      >
        ×
      </button>
    </div>
    <div className="nx-ask-loading" role="status">
      <span className="sr-only">Loading your coach</span>
      <div className="sg-skeleton h-4 w-2/3" />
      <div className="sg-skeleton h-16 w-full" />
      <div className="sg-skeleton h-4 w-1/2" />
    </div>
  </aside>
);

const AskNexusPanel = () => {
  const { isSignedIn, user } = useAuth() || {};
  const [open, setOpenState] = useState(isAskNexusOpen);
  // Slide in only when opened here; a panel that stays open across a route
  // (every route mounts its own Layout) must not replay its entrance.
  const [entering, setEntering] = useState(false);
  const returnFocusRef = useRef(null);
  // Leaves with the sheet exit (24 px slide back + fade, 140 ms).
  const presence = usePresence(open, { exitMs: MS.exitSlow });

  const setOpen = useCallback((next) => {
    setOpenState((previous) => {
      const value = typeof next === "function" ? next(previous) : next;
      rememberAskNexusOpen(value);
      if (value && !previous) {
        const active = document.activeElement;
        returnFocusRef.current = active instanceof HTMLElement ? active : null;
        setEntering(true);
      }
      return value;
    });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    if (target?.isConnected) window.requestAnimationFrame(() => target.focus());
  }, [setOpen]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => close();
    const onKeyDown = (event) => {
      if (!isToggleKey(event)) return;
      event.preventDefault();
      if (isAskNexusOpen()) close();
      else setOpen(true);
    };
    window.addEventListener(ASK_NEXUS_OPEN_EVENT, onOpen);
    window.addEventListener(ASK_NEXUS_CLOSE_EVENT, onClose);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener(ASK_NEXUS_OPEN_EVENT, onOpen);
      window.removeEventListener(ASK_NEXUS_CLOSE_EVENT, onClose);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [close, setOpen]);

  if (!presence.mounted || (isSignedIn === false && !user)) return null;

  return (
    <div
      className="nx-ask-layer"
      data-state={presence.state}
      data-enter={entering || undefined}
      onAnimationEnd={(event) => {
        if (!event.target.classList?.contains("nx-ask")) return;
        if (presence.isClosing) presence.onAnimationEnd();
        else setEntering(false);
      }}
    >
      <Suspense fallback={<PanelLoading onClose={close} />}>
        <AskNexus onClose={close} />
      </Suspense>
    </div>
  );
};

export default AskNexusPanel;
